import Image from 'next/image'
import {FC} from 'react'

import {Button} from '@/components/common/content/Button'
import {ButtonVariant} from '@/components/common/content/ButtonTypes'
import {useHomepageContent} from '@/content/homepage'
import {Link} from '@/i18n'
import {theme} from '@/theme/theme'
import {QueryLink} from '@/utils/constants'
import {MOBILE_SCREENSIZE, TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'
import {analytics} from '@/utils/tracking/analytics'

import {Section} from '../content/Section'
import {Text, TextVariant} from '../content/Text'
import {ArrowIcon} from '../icons/ArrowIcon'

type GetStartedWithVerificationBannerProps = {
  title: string
  primaryButtonLink: string | QueryLink
  primaryButtonText: string
  primaryButtonTrackedEvent: string
  secondaryButtonLink?: string | QueryLink
  secondaryButtonText?: string
  secondaryButtonTrackedEvent?: string
  secondaryButtonOnClick?: () => void
  hasPaddingTop?: boolean
}

export const GetStartedWithVerificationBanner: FC<
  React.PropsWithChildren<GetStartedWithVerificationBannerProps>
> = ({
  title,
  primaryButtonLink,
  primaryButtonText,
  primaryButtonTrackedEvent,
  secondaryButtonLink,
  secondaryButtonText,
  secondaryButtonTrackedEvent,
  secondaryButtonOnClick,
  hasPaddingTop,
}) => {
  const {imageSrc, imageAlt} = useHomepageContent().getStartedBanner
  return (
    <>
      <Section hasPaddingTop={hasPaddingTop}>
        <div className="wrapper">
          <div className="content">
            <Text variant={TextVariant.h1} element="h2">
              {title}
            </Text>
            <div className="buttons">
              <div className="button-wrapper">
                <Link
                  href={primaryButtonLink}
                  onClick={() => analytics.track(primaryButtonTrackedEvent)}
                >
                  <Button
                    variant={ButtonVariant.primary}
                    type="button"
                    icon={<ArrowIcon color={theme.palette.text.white} />}
                    isIconAnimated
                  >
                    {primaryButtonText}
                  </Button>
                </Link>
              </div>
              {secondaryButtonLink && secondaryButtonText ? (
                <div className="button-wrapper">
                  <Link
                    href={secondaryButtonLink}
                    onClick={
                      secondaryButtonTrackedEvent
                        ? () => analytics.track(secondaryButtonTrackedEvent)
                        : undefined
                    }
                  >
                    <Button variant={ButtonVariant.secondary} type="button">
                      {secondaryButtonText}
                    </Button>
                  </Link>
                </div>
              ) : secondaryButtonOnClick && secondaryButtonText ? (
                <div className="button-wrapper">
                  <Button
                    variant={ButtonVariant.secondary}
                    type="button"
                    onClick={() => {
                      secondaryButtonOnClick()
                      secondaryButtonTrackedEvent && analytics.track(secondaryButtonTrackedEvent)
                    }}
                  >
                    {secondaryButtonText}
                  </Button>
                </div>
              ) : null}
            </div>
          </div>
          <div className="image">
            <Image
              src={imageSrc}
              alt={imageAlt}
              style={{objectFit: 'contain'}}
              fill
              sizes={`${MOBILE_SCREENSIZE} 160px, ${TABLET_SCREENSIZE} 300px, 400px`}
            />
          </div>
        </div>
      </Section>

      <style jsx>{`
        .wrapper {
          display: flex;
          justify-content: space-between;
          position: relative;
          padding: ${theme.spacing[7]};
          background-color: ${theme.palette.background.butter};
          border-top-left-radius: 16px;
          border-top-right-radius: 16px;
        }

        .content {
          flex: 1;
          max-width: 470px;
        }

        .buttons {
          display: flex;
          margin-top: ${theme.spacing[5]};
        }

        .button-wrapper + .button-wrapper {
          margin-left: ${theme.spacing[3]};
        }

        .image {
          width: 425px;
          height: 400px;
          position: absolute;
          right: 90px;
          bottom: -${theme.spacing[7]};
          z-index: 1;
        }

        @media ${TABLET_SCREENSIZE} {
          .wrapper {
            flex-direction: column;
            align-items: center;
            padding-bottom: 0;
          }

          .content {
            text-align: center;
            max-width: 600px;
          }

          .buttons {
            justify-content: center;
          }

          .image {
            position: relative;
            right: 0;
            bottom: 0;
            width: 300px;
            height: 280px;
            margin-top: ${theme.spacing[5]};
            margin-bottom: -44px;
          }
        }

        @media ${MOBILE_SCREENSIZE} {
          .wrapper {
            padding: ${theme.spacing[6]} ${theme.spacing[4]} 0;
          }

          .buttons {
            display: block;
            margin-top: ${theme.spacing[4]};
          }

          .button-wrapper + .button-wrapper {
            margin-left: 0;
            margin-top: ${theme.spacing[3]};
          }

          .image {
            width: 160px;
            height: 150px;
            margin-top: ${theme.spacing[5]};
            margin-bottom: -${theme.spacing[4]};
          }
        }
      `}</style>
    </>
  )
}
